import useAppState from "context/useAppState";
import { useState, useRef, useEffect } from "react";

import imageIcon from "../../../assets/images/image-icon.svg";
import microphoneIcon from "../../../assets/images/microphone-icon.svg";
import sendIcon from "../../../assets/images/search-bar-icon.svg";
import { isNewFete } from "utils/fete";
import { CHAT_SESSION, DEFAULT_FETEBUDDY_INITIATE_MESSAGES, DEFAULT_FETEBUDDY_PROMPT_KEYS, MESSAGE_TYPES } from "constants/llm";
import { useNavigate, useParams } from "react-router-dom";
import { LLM_INTENTS, PROMPT_DEFAULTS, getAllCountryAbbreviation, timeZoneWithStates } from "utils/constant";
import { getLLMAgentRAGCreatePayload } from "services/llm/payloads";
import { cleanObject, getTimeForChatMessage, jsonToMarkdown } from "utils/function";
import { getAnswerFromResponse, getChatHistoryFromResponse, getFormattedHistory, getFullDateAndTimeFormatted } from "utils/llm";
import { City, Country } from "country-state-city";
import { cloneDeep } from "lodash";


const ChatInput = () => {
    const [userChatInput, setUserChatInput] = useState('');
    const {
        setState: setChatboxState, getLLMResponse, updateExistingEvent, cardPrompt, currentSession, getNewTitleForSession
    } = useAppState("chatBox");
    const {
        setState: setChatMessagesState, messageHistory, getUserChatSessionMessages, updateSessionTitle, getUserChatSessions, createSession
    } = useAppState('chatMessages');
    const { setState: setNewFeteState, newFeteDetails, draftEvent, draftEventTasks } = useAppState('newFeteChat');
    const {
        setState: setEventDataState,
        eventFetchedData,
        callFromEmptyReviewSpace,
        showReviewSpace,
        brainStormText,
        insightEditData,
        sendButtonRef,
        updateEventCallApiFlag,
        eventTasks,
        bestActionQuery,
        bestActionPrompt
    } = useAppState('eventData');
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const navigate = useNavigate();

    const { id, draftEventId } = useParams();
    const { selectedCardDetail } = useAppState('cardWrapper');
    const { extractFeteOverviewDetails } = useAppState('newFeteChat');

    const userData = JSON.parse(localStorage.getItem('userData') as string)
    const currentTeam = JSON.parse(localStorage.getItem('selectedTeam') as string)

    let currentSelectedSession: any = null

    const countryCode = "US";
    const country: any = Country.getCountryByCode(countryCode);
    const cities: any = City.getCitiesOfCountry(country.isoCode);

    const allCountries: any = getAllCountryAbbreviation();

    const options = cities.map((item: any) => ({
        label: `${item.name} , ${item.stateCode} , ${item.countryCode}`,
        value: item.name,
    }));
    const stateTimezones: any[] = timeZoneWithStates;

    const getPromptKey = () => {
        if (isNewFete()) {
            return DEFAULT_FETEBUDDY_PROMPT_KEYS.START;
        }
        if (cardPrompt?.name) {
            return cardPrompt?.name;
        } else {
            return DEFAULT_FETEBUDDY_PROMPT_KEYS.CONTINUE;
        }
    }

    const getMetadataFilter = () => {
        let metadata_filter: any = {
            teamId: currentTeam?.currentTeamId,
            userId: userData?.id
        }
        if (id || draftEvent?._id) {
            metadata_filter['eventId'] = id || draftEvent?._id
        }
        if (isNewFete()) {
            return metadata_filter;
        }
        if (selectedCardDetail) {
            metadata_filter['cardId'] = selectedCardDetail?.id
        }
        return metadata_filter;
    }

    const getMetadataFilterForLLM = () => {
        let metadata_filter: any = {
            teamId: currentTeam?.currentTeamId,
        }
        if (id || draftEvent?._id) {
            metadata_filter['eventId'] = id || draftEvent?._id
        }
        if (isNewFete()) {
            return metadata_filter;
        }
        return metadata_filter;
    }

    const getTasksInput = () => {
        if (selectedCardDetail) {
            return jsonToMarkdown(selectedCardDetail?.tasks)
        }
        if (id) {
            return jsonToMarkdown(eventTasks)
        }
        if (draftEventTasks && draftEventTasks?.length > 0) {
            return jsonToMarkdown(draftEventTasks)
        }
        return null;
    }

    const extractAndUpdateNewFeteOverview = async (userMessage: any) => {
        const extractedInfo = await extractFeteOverviewDetails({
            input_variables: {
                "question": userMessage,
            }
        });
        let cleanedObject = cleanObject(extractedInfo);

        // Set event location, date, times, timezone and other dynamic values
        const mergedObject = { ...newFeteDetails, ...cleanedObject }
        cleanedObject = { ...cleanedObject, ...setUpdatedEventData(mergedObject) }

        console.log({ cleanedObject })

        setNewFeteState({ newFeteDetails: { ...newFeteDetails, ...cleanedObject } })
        await updateExistingEvent(draftEvent?._id || id, { ...cleanedObject, is_draft: id ? false : true });
        if (id && !eventFetchedData?.is_draft) {
            // Fetch updated event details for actual event
            updateEventCallApiFlag(true);
        }
    }

    const handleCreateNewFete = async () => {
        const updateRes = await updateExistingEvent(draftEvent?._id, { ...newFeteDetails, is_draft: false });
        if (updateRes?.data && updateRes?.data?._id) {
            navigate(`/fete/${updateRes?.data?._id}`)
        }
    }


    const setUpdatedEventData = (mergedObject: any) => {
        let cleanedObject = cloneDeep(mergedObject)

        // Append time zone from location
        if (cleanedObject?.event_location) {
            const timeZone = getTimeZoneValue(cleanedObject?.event_location);
            if (timeZone) {
                cleanedObject = { ...cleanedObject, event_time_zone: timeZone }
            }
        }

        // Set correct start date
        if (cleanedObject?.event_start_date) {
            const startDate = setDateFormat(cleanedObject?.event_start_date)
            cleanedObject = { ...cleanedObject, event_start_date: startDate }
        }

        // Set correct end date
        if (cleanedObject?.event_end_date) {
            const endDate = setDateFormat(cleanedObject?.event_end_date)
            cleanedObject = { ...cleanedObject, event_end_date: endDate }
        }

        // Set correct times array
        if (cleanedObject?.event_start_date || cleanedObject?.event_end_date) {
            const times = addEventTime(cleanedObject.event_start_date, cleanedObject.event_end_date)
            cleanedObject = { ...cleanedObject, times }
        }

        return cleanedObject
    }

    const setDateFormat = (date: string) => {
        let updatedDate;

        // To check date format from LLM, if it's YYYY-MM-DD, need to add the time string, if it is YYYY/MM/DD, it is fine
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            const localDateString = `${date}T00:00:00`;
            updatedDate = new Date(localDateString);
        } else {
            updatedDate = new Date(date);
        }

        updatedDate.setHours(0, 0, 0, 0);
        return updatedDate.getTime()
    }

    const getTimeZoneValue = (locations: any) => {
        if (locations) {
            const allLocations = locations.split(';')
            if (allLocations?.length) {
                let selectedOption = allLocations.map((location: any) =>
					options.find((option: any) => option.label?.replace(/\s*,\s*/g, ', ').toLowerCase().startsWith(location.trim().toLowerCase()))
				).filter(Boolean);

				if (!selectedOption?.length) {
					selectedOption = allLocations.map((location: any) =>
						options.find((option: any) => option.label?.toLowerCase().startsWith(location?.split(',')?.[0].trim()?.toLowerCase()))
					);
				}

                const cityISOCode = selectedOption?.length ? selectedOption[0].label.match(/\b[A-Z]{2}\b/)[0] : null;
                const cityTimeZone = stateTimezones.find(st => st.states.includes(cityISOCode))?.timezone || null;
                
                return cityTimeZone
            }
        }
        return null
    }

    const addEventTime = (startDate?: any, endDate?: any) => {
        let eventData: any = []
        if ((startDate && !endDate) || (!startDate && endDate) || startDate === endDate) {
            eventData.push({ startDate: (startDate || endDate), startTime: '09:00', endTime: '16:00' })
        }

        if (startDate && endDate && startDate != endDate) {
            const defaultStartDate = Math.min(startDate, endDate)
            const defaultEndDate = Math.max(startDate, endDate);

            const date1: any = new Date(defaultStartDate);
            const date2: any = new Date(defaultEndDate);

            const differenceInMs: any = date2 - date1;
            const totalDays = Math.floor(differenceInMs / (1000 * 60 * 60 * 24)) + 1;

            for (let i = 0; i < totalDays; i++) {
                const currentDate = new Date(startDate);
                currentDate.setDate(currentDate.getDate() + i);
                eventData.push({ startDate: currentDate.getTime(), startTime: '09:00', endTime: '16:00' })
            }
        }

        return eventData;
    }

    // const handleCreateNewFete = async () => {
    //     console.log('117')
    //     const updateRes = await updateExistingEvent(draftEvent?._id, { is_draft: false });
    //     if (updateRes?.data && updateRes?.data?._id) {
    //         navigate(`/fete/${updateRes?.data?._id}`)
    //     }
    // }

    const getChatSessions = async () => {
        const userSessions = await getUserChatSessions({
            page: 0,
            perPage: 10,
            filter: getMetadataFilter(),
        });
        return userSessions;
    }

    const getLLMInputVariables = () => {
        const vars: any = {
            is_vendor: true,
            json_formats: PROMPT_DEFAULTS.JSON_FORMATS,
            notes: PROMPT_DEFAULTS.NOTES
        };
        if (eventFetchedData) {
            vars['event_details'] = jsonToMarkdown(eventFetchedData)
        }
        if (getTasksInput() !== '') {
            vars['task_list'] = getTasksInput()
        }
        return vars;
    }

    const getBestActionPrompt = () => {
        const promptKey = bestActionPrompt ? bestActionPrompt : getPromptKey();
        return promptKey
    }

    const sendChat = async (text?: any) => {
        if (userChatInput.trim().length || text?.trim()?.length) {
            try {
                const userMessage = userChatInput.trim() || text?.trim();
                setChatMessagesState({ messageHistory: [...messageHistory, { type: MESSAGE_TYPES.HUMAN, content: userMessage, createdAt: new Date().toISOString() }], showChatLoader: true })
                setUserChatInput('');
                clearTextarea();
                const llmRes = await getLLMResponse({
                    input_variables: getLLMInputVariables(),
                    userMessage: userMessage,
                    // promptKey: getPromptKey(),
                    promptKey: getBestActionPrompt(),
                    session_id: currentSession?._id ? currentSession._id : currentSelectedSession?._id,
                    metadata_filter: selectedCardDetail?.title?.toLowerCase() == 'vendor' ? { eventId: id } : getMetadataFilterForLLM()
                });
                let answer = getAnswerFromResponse(llmRes);
                let shouldCreateFete = false;
                let shouldSkipExtraction = false;
                if (answer && answer.includes(LLM_INTENTS.CREATE_EVENT)) {
                    shouldCreateFete = true;
                }
                if (answer && answer.includes(LLM_INTENTS.SKIP_EXTRACTION)) {
                    shouldSkipExtraction = true;
                }
                const chatSessionMessages = currentSession?._id ? await getUserChatSessionMessages(currentSession?._id) : await getUserChatSessionMessages(currentSelectedSession?._id)
                setChatMessagesState({ messageHistory: [...chatSessionMessages], showChatLoader: false })
                if (shouldCreateFete) {
                    await handleCreateNewFete();
                }
                if (!shouldCreateFete && !shouldSkipExtraction) {
                    // Do not extract event details when chat through planning board's cards
                    if (!selectedCardDetail?.title) {
                        await extractAndUpdateNewFeteOverview(userMessage);
                    }
                }
                if (!isNewFete() && (currentSession?.title === CHAT_SESSION.DRAFT_TITLE || currentSelectedSession?.title === CHAT_SESSION.DRAFT_TITLE)) {
                    const newTitle = await getNewTitleForSession({ chat_history: chatSessionMessages, session_id: currentSession?._id || currentSelectedSession?._id, context: jsonToMarkdown(eventFetchedData) })
                    await updateSessionTitle(currentSession?._id || currentSelectedSession?._id, newTitle)
                    const userSessions = await getChatSessions();

                    const latestSession: any = currentSession || currentSelectedSession
                    // setChatboxState({ currentSession: { ...currentSession, 'title': newTitle }, userChatSessions: userSessions?.sessions || [] })
                    setChatboxState({ currentSession: { ...latestSession, 'title': newTitle }, userChatSessions: userSessions?.sessions || [] })
                }
                setEventDataState({ bestActionPrompt: null })
            } catch (error) {
                setChatMessagesState({ showChatLoader: false })
                setEventDataState({ bestActionPrompt: null })
            }
        }
    };

    const handleKeyDown = (event: any) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            sendChat();
        }
    };

    const handleOnInput = (event: any) => {
        if (textareaRef.current) {
            if (!event.target.value) {
                textareaRef.current.style.height = '24px';
            }
        }
    };

    const clearTextarea = () => {
        setUserChatInput("");
        if (textareaRef.current) {
            textareaRef.current.style.height = '24px';
        }
    };

    const adjustTextareaHeight = (textarea: any) => {
        textarea.style.height = `${textarea.scrollHeight}px`;
    };

    const useChatInputChange = (event: any) => {
        setUserChatInput(event.target.value);
        adjustTextareaHeight(event.target);
    };

    // For Direct Calling
    useEffect(() => {
        if (callFromEmptyReviewSpace) {
            if (brainStormText) {
                // When cliking brainstorm button from empty review space
                if (sendButtonRef.current) {  // To prevent calling sendChat() twice
                    manageOutsideCalls()
                    sendButtonRef.current = false;
                }
            } else {
                // Set message data in chat, when clicking edit icon from any task from review space or board data
                setChatMessagesState({ messageHistory: [...insightEditData], showChatLoader: false })
                if (sendButtonRef.current) {
                    createNewSession()
                    sendButtonRef.current = false;
                }
            }
            setEventDataState({ callFromEmptyReviewSpace: false, brainStormText: null, insightEditData: [] })
        }
    }, [callFromEmptyReviewSpace, brainStormText])

    const manageOutsideCalls = async () => {
        // await createNewSession()
        sendChat(brainStormText)
    }

    const createNewSession = async () => {
        let sessionRes = await createSession({
            title: CHAT_SESSION.DRAFT_TITLE,
            metadata: getNewMetadataFilter({ eventId: id })
        });

        currentSelectedSession = sessionRes;

        const userSessions = await getChatSessions();
        setChatboxState({ currentSession: sessionRes, userChatSessions: userSessions?.sessions || [] })
    }

    const getNewMetadataFilter = ({ ...additional }: any) => {
        return {
            teamId: currentTeam?.currentTeamId,
            userId: userData?.id,
            ...additional,
        }
    }
    // For Direct Calling

    useEffect(() => {
        if (bestActionQuery) {
            if (sendButtonRef.current) {  // To prevent calling sendChat() twice
                sendChat(bestActionQuery)
                sendButtonRef.current = false;
                setEventDataState({ bestActionQuery: null })
            }
        }
    }, [bestActionQuery])

    return (
        <div className="flex items-center gap-x-2 pb-4 pt-3">
            <div className="border border-[#D0D5DD] hover:border-[#1E0AF5] focus-within:border-[#1E0AF5] bg-[#fff] rounded-lg py-2.5 px-3.5 flex flex-1 items-center shadow-customShadow4">
                <textarea id="chatArea" className="w-full placeholder:text-[#344054] placeholder:text-base text-base text-[#344054] h-[24px] resize-none pr-2" placeholder="Ask anything or choose an option below"
                    value={userChatInput}
                    onChange={useChatInputChange}
                    onKeyDown={handleKeyDown}
                    onInput={handleOnInput}
                    ref={textareaRef}
                />
                {/* <div className="flex gap-x-2 items-center">
                    <img className="w-4 h-4 flex-shrink-0 cursor-pointer" src={imageIcon} alt="" />
                    <img
                        className="w-4 h-4 flex-shrink-0 cursor-pointer"
                        src={microphoneIcon}
                        alt=""
                    />
                </div> */}
            </div>
            <button className="w-11 h-11 flex-shrink-0 flex items-center justify-center bg-[#1E0AF5] hover:bg-[#120693] rounded-lg shadow-customShadow4"
                onClick={() => sendChat()}
            >
                <img className="w-5 h-5 flex-shrink-0" src={sendIcon} alt="" />
            </button>
        </div>
    )
}

export default ChatInput;